/**
 * useCandidatosElemento.ts
 *
 * P2RED-S1-5a (DOP-S1-5A-ALCANCE=c): flujo de datos de candidatos de
 * geocodificación para el elemento seleccionado en P2 Valida.
 *
 * Fuente: búsqueda on-demand vía `searchLocal` (LocalDataService, datos DERA
 * locales) al cambiar la selección — precedente vivo del Step2 legacy (F2.3).
 * Los candidatos NO viajan por el pipeline (ProcessedInfrastructure no los
 * porta; el orquestador los descarta tras `disambiguate`): se recuperan aquí.
 *
 * Aplicar un candidato usa el canal C2 (`handleApplyCoords`) con
 * `editSource: 'assisted'` (DOP-S1-5A-CANAL=a) — mismo valor que ya persiste
 * `handleConfirmElement`, sin ampliar el contrato del Worker.
 *
 * Sin superficie visible (DOP-S1-5A-UI=a): el ploteo llega en S1-5b.
 *
 * @module wizard-v2/step2-valida/useCandidatosElemento
 */

import { useEffect, useRef, useState } from 'react';
import { searchLocal, type LocalFeature } from '../../../lib/LocalDataService';
import type { GeoCandidate } from './types';

// ============================================================================
// CONSTANTES (calibración heredada del precedente legacy F2.3)
// ============================================================================

const MAX_CANDIDATOS = 5;
const UMBRAL_FUZZY = 0.3;

// ============================================================================
// MAPEO LocalFeature → GeoCandidate
// ============================================================================

/**
 * Mapea una LocalFeature de `searchLocal` al contrato `GeoCandidate` que
 * consume la UI de step2-valida (CandidatesList / mapa en S1-5b).
 *
 * Nota P11 (firma real verificada): LocalFeature expone `x`/`y` en
 * EPSG:25830 — NO `utmX`/`utmY` como asumía el filtro defensivo del legacy.
 */
export function toGeoCandidate(
  f: LocalFeature,
  infraId: string,
  idx: number,
  matchScore: number,
): GeoCandidate {
  return {
    id: `${infraId}-cand-${idx}`,
    address: f.direccion || f.nombre,
    utmX: f.x,
    utmY: f.y,
    source: 'LOCAL_DERA',
    // Clamp 50-95 heredado del precedente legacy (F2.9b)
    confidence: Math.max(50, Math.min(95, matchScore || 70)),
    isBest: idx === 0,
  };
}

// ============================================================================
// APLICAR CANDIDATO (canal C2 · editSource 'assisted')
// ============================================================================

/** Firma del canal C2 tal y como la expone WizardPageV2.handleApplyCoords. */
export type AplicarCoordsFn = (
  infraId: string,
  x: number,
  y: number,
  source?: 'manual' | 'map' | 'assisted',
) => void;

/**
 * Aplica las coordenadas de un candidato al elemento por el canal C2 con
 * `editSource: 'assisted'` (validación asistida: el sistema propone, el
 * técnico elige). Función pura de despacho — testeable sin jsdom.
 */
export function aplicarCandidato(
  infraId: string,
  candidato: GeoCandidate,
  onApply: AplicarCoordsFn,
): void {
  onApply(infraId, candidato.utmX, candidato.utmY, 'assisted');
}

// ============================================================================
// HOOK
// ============================================================================

export interface UseCandidatosElementoParams {
  /** Id del elemento seleccionado; null/undefined limpia el estado. */
  infraId: string | null | undefined;
  /** Nombre a buscar (fuzzy). Vacío → sin búsqueda. */
  nombre: string;
  /** Municipio (alternativa a codIne para acotar). */
  municipio?: string;
  /** Código INE 5 dígitos (acotación preferente). */
  codIne?: string;
}

export interface UseCandidatosElementoResult {
  /** Candidatos del elemento seleccionado (vacío si no hay o aún carga). */
  candidatos: GeoCandidate[];
  /** true mientras la búsqueda del elemento actual está en vuelo. */
  cargando: boolean;
}

/**
 * Recupera on-demand los candidatos de geocodificación del elemento
 * seleccionado. Cachea por infraId (una búsqueda por elemento y sesión de
 * página). Guard de montaje + token de vigencia: una resolución tardía de un
 * elemento ya deseleccionado no pisa el estado (lección FIX-WINDOW/S2WG).
 */
export function useCandidatosElemento(
  params: UseCandidatosElementoParams,
): UseCandidatosElementoResult {
  const { infraId, nombre, municipio, codIne } = params;
  const [candidatos, setCandidatos] = useState<GeoCandidate[]>([]);
  const [cargando, setCargando] = useState(false);
  const cacheRef = useRef<Map<string, GeoCandidate[]>>(new Map());
  const montadoRef = useRef(true);
  const vigenteRef = useRef<string | null>(null);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  useEffect(() => {
    vigenteRef.current = infraId ?? null;

    if (infraId == null || !nombre) {
      setCandidatos([]);
      setCargando(false);
      return;
    }

    const enCache = cacheRef.current.get(infraId);
    if (enCache) {
      setCandidatos(enCache);
      setCargando(false);
      return;
    }

    setCargando(true);
    void (async () => {
      let resultado: GeoCandidate[] = [];
      try {
        const res = await searchLocal({
          nombre,
          ...(codIne ? { codMun: codIne } : {}),
          ...(municipio ? { municipio } : {}),
          maxResults: MAX_CANDIDATOS,
          threshold: UMBRAL_FUZZY,
        });
        resultado = (res?.features ?? [])
          .filter((f) => typeof f.x === 'number' && typeof f.y === 'number')
          .map((f, idx) => toGeoCandidate(f, infraId, idx, res.matchScore));
      } catch {
        resultado = []; // fallo de búsqueda → sin candidatos, nunca rompe la ficha
      }
      cacheRef.current.set(infraId, resultado);
      // Solo publicar si el componente sigue montado Y el elemento sigue seleccionado
      if (montadoRef.current && vigenteRef.current === infraId) {
        setCandidatos(resultado);
        setCargando(false);
      }
    })();
  }, [infraId, nombre, municipio, codIne]);

  return { candidatos, cargando };
}
